import { z } from "zod";

/**
 * US-E24.F52-0504 (@UC9591) — NHÃN HIỂN THỊ của một dòng hoạt động "đã nộp quỹ".
 *
 * 🔴🔴 VÌ SAO KHÔNG SỬA THẲNG `ledger_transactions.memo`: bảng đó là **1 trong 9 bảng append-only**
 * (`reject_mutation()` cấm UPDATE/DELETE ở tầng DB) và **KHÔNG ĐƯỢC MỞ**. Nhãn là thứ người dùng
 * đổi tới đổi lui — bản chất LIFECYCLE — nên nó sống ở **bảng riêng đặt CẠNH sổ cái**
 * (`ledger_transaction_labels`), 0 cột mới trên sổ, 0 trigger nào bị nới.
 *
 * 🔴 NÓ LÀ CHỮ, KHÔNG PHẢI TIỀN. Hợp đồng này không có một field `*Minor` nào và không bao giờ
 * được có: đổi nhãn KHÔNG đổi một đồng nào của sổ. Sửa SỐ TIỀN vẫn đi đúng một đường
 * `POST /v1/funds/:id/ledger-corrections` (REVERSAL + POST trong một transaction).
 *
 * 🔴 MẶC ĐỊNH LÀ TÊN ĐỢT THU, KHÔNG PHẢI CHUỖI RỖNG. Chưa ai đặt nhãn ⇒ đọc ra `Collection.title`
 * của đợt thu sinh ra bút toán. Và đặt nhãn cho MỘT dòng **không** đổi tên đợt thu của cả nhóm —
 * đó là hai thứ khác nhau, gộp lại là một người sửa dòng của mình rồi cả nhóm thấy tên đợt đổi.
 */

/** Trần độ dài nhãn — mirror `Collection.title` (nhãn thay thế tên đợt thu, không dài hơn). */
export const LEDGER_TX_LABEL_MAX = 200;

/**
 * Body `PUT /v1/funds/:id/transactions/:txId/label`.
 *
 * 🔴 GỬI RỖNG = **XOÁ NHÃN** (quay về mặc định), KHÔNG phải "đặt nhãn = chuỗi rỗng". Vì vậy schema
 * CHO PHÉP chuỗi rỗng ở biên (đó là một ý định hợp lệ) và `.trim()` để `"   "` cũng là xoá — còn
 * tầng DB thì có CHECK cấm lưu nhãn rỗng, nên "rỗng" không bao giờ tồn tại ở dạng dữ liệu.
 */
export const putTransactionLabelRequestSchema = z
  .object({
    label: z.string().trim().max(LEDGER_TX_LABEL_MAX),
  })
  .strict();

export type PutTransactionLabelRequest = z.infer<typeof putTransactionLabelRequestSchema>;

/**
 * Nhãn HIỆU LỰC của một bút toán.
 *
 * `isDefault` là thứ client cần để biết mình đang nhìn nhãn RIÊNG hay tên đợt thu mượn tạm — thiếu
 * nó thì ô "Tiêu đề" trên form không phân biệt được *"chưa ai đặt"* với *"đã đặt trùng tên đợt"*,
 * và bấm Lưu sẽ ghi một nhãn mà người dùng không hề gõ.
 * `label = null` ⇒ không nhãn riêng VÀ không đợt thu nào để mượn tên (KHÔNG bịa chuỗi rỗng).
 */
export const transactionLabelResponseSchema = z
  .object({
    txId: z.string().uuid(),
    label: z.string().nullable(),
    isDefault: z.boolean(),
  })
  .strict();

export type TransactionLabelResponse = z.infer<typeof transactionLabelResponseSchema>;
